import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Calendar, Heart, MapPin, Star, User, Crown } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

export const ListingList = ({
  id,
  title,
  description,
  imageUrl,
  location,
  date,
  category,
  userName,
  userAvatar,
  userRating = 0,
  isOwner = false,
  currentUserId = null,
  ownerId = null,
  status = "active", // New prop for listing status
  isFoundingMember = false // New prop for founding member status
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  // Check if listing is inactive
  const isInactive = status === "inactive";

  // Truncate username to 15 characters
  const truncatedUserName = userName.length > 15 ? userName.slice(0, 15) + "..." : userName;

  const handleLikeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  // Wrapper component for conditional linking
  const CardWrapper = ({ children }) => {
    if (isInactive) {
      return (
        <div className="block h-full cursor-not-allowed">
          {children}
        </div>
      );
    }
    return (
      <Link to={`/listing/${id}`} className="block h-full">
        {children}
      </Link>
    );
  };

  // Helper function to safely get location string
  const getLocationString = () => {
    if (!location) return "Location not specified";
    
    // If location is populated correctly: location is suburb object
    if (location.name && location.city?.name) {
      return `${location.name}, ${location.city.name}`;
    }
    
    // Fallback if location is just an ID string
    if (typeof location === 'string') {
      return "Location ID: " + location.substring(0, 8) + "...";
    }
    
    return "Location not available";
  };

  return (
    <div className="w-full mb-4">
      <Card className={`group transition-all duration-300 border-0 shadow-sm bg-white h-25 ${
        isInactive 
          ? 'opacity-60 grayscale-[50%] cursor-not-allowed' 
          : 'hover:shadow-lg hover:-translate-y-1'
      }`}>
        <CardContent className="!p-0 h-full">
          <CardWrapper>
            <div className="flex gap-4 p-4">
              {/* Image Section */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100">
                  <img
                    src={imageUrl}
                    alt={title}
                    className={`w-full h-full object-cover transition-transform duration-300 ${
                      !isInactive ? 'group-hover:scale-105' : ''
                    }`}
                    onLoad={() => setImageLoaded(true)}
                  />
                  {!imageLoaded && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-xl" />
                  )}
                </div>
                {/* Category Badge on Image */}
                <Badge 
                  variant="secondary" 
                  className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-1 shadow-md"
                >
                  {category?.name}
                </Badge>
                {/* Inactive Badge */}
                {isInactive && (
                  <Badge 
                    variant="secondary" 
                    className="absolute -bottom-2 -right-2 backdrop-blur-sm bg-gray-500/80 text-white text-xs px-2 py-1"
                  >
                    Inactive
                  </Badge>
                )}
              </div>

              {/* Content Section */}
              <div className="flex-1 min-w-0 flex flex-col justify-between overflow-hidden">
                {/* Row 1: Title + Like */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`font-semibold text-lg transition-colors duration-200 line-clamp-1 ${
                    isInactive 
                      ? 'text-gray-500' 
                      : 'text-slate-800 group-hover:text-emerald-600'
                  }`}>
                    {title}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-shrink-0 p-1 hover:bg-red-50 rounded-full"
                    onClick={handleLikeClick}
                    disabled={isInactive}
                  >
                    <Heart 
                      className={`w-4 h-4 transition-colors ${
                        isLiked ? 'fill-red-500 text-red-500' : 'text-gray-400 hover:text-red-500'
                      } ${isInactive ? 'opacity-50' : ''}`}
                    />
                  </Button>
                </div>

                {/* Row 2: Description */}
                <p className={`text-sm line-clamp-2 ${
                  isInactive ? 'text-gray-400' : 'text-gray-600'
                }`}>
                   {description.length > 50 ? description.slice(0, 50) + "..." : description}
                </p>

                {/* Bottom line with all info */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span className={`font-medium ${
                      isInactive ? 'text-gray-400' : 'text-emerald-600'
                    }`}>
                      {getLocationString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span className={isInactive ? 'text-gray-400' : ''}>
                      {date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                      {userAvatar ? (
                        <img src={userAvatar} alt={userName} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        isInactive ? 'text-gray-400' : 'text-gray-700'
                      }`} title={userName}>
                        {truncatedUserName}
                      </span>
                      {isFoundingMember && (
                        <Badge className={`text-xs px-1.5 py-0.5 gap-1 ${
                          isInactive 
                            ? 'bg-amber-100/50 text-amber-600/50 border-amber-200/50' 
                            : 'bg-amber-100 text-amber-800 border-amber-200'
                        }`}>
                          <Crown className="h-2.5 w-2.5" />
                        </Badge>
                      )}
                    </div>
                    {userRating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className={`w-3 h-3 fill-yellow-400 text-yellow-400 ${
                          isInactive ? 'opacity-50' : ''
                        }`} />
                        <span className={`text-xs ${
                          isInactive ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {userRating.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardWrapper>
        </CardContent>
      </Card>
    </div>
  );
};